import IconEdit from '@apollo/icons/default/IconEdit.svg';
import IconPlus from '@apollo/icons/default/IconPlus.svg';
import { Button } from '@apollo/orbit';
import classnames from 'classnames';
import * as monacoEditor from 'monaco-editor';
import React from 'react';
import { Link, useLocation, useRouteMatch } from 'react-router-dom';

import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import {
  MONACO_READONLY_OPTIONS,
  StudioEditorContainer,
  useStudioEditor,
} from 'src/hooks/useStudioEditor';
import Config from 'src/lib/config';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { useGraphRef } from '../../hooks/useGraphRef';
import { useIsLoggedIn } from '../../hooks/useIsLoggedIn';
import { sandboxGraphRouteConfig } from '../../routes';
import { SandboxLogInButton } from '../../sandboxShared/SandboxSignUpButtons';
import { ExplorerDocsTab } from '../helpers/ExplorerDocsTab';

export const ScriptPane = ({
  scriptEditor,
  isOperationScriptEnabled,
  setDocsTab,
  scriptType,
}: {
  scriptEditor: monacoEditor.editor.IStandaloneCodeEditor;
  isOperationScriptEnabled: boolean;
  setDocsTab: (newDocsTab: ExplorerDocsTab) => void;
  scriptType: 'preflightOperation' | 'postflightOperation';
}) => {
  const isSandboxRoute =
    useRouteMatch(sandboxGraphRouteConfig.definition) !== null;
  const { isLoggedIn } = useIsLoggedIn();
  const graphRef = useGraphRef();
  const location = useLocation<{
    referrer: 'tab' | 'settings';
  }>();

  const currentTabOperationScriptValue = scriptEditor.getValue();

  const readOnlyOperationScriptsEditor = useStudioEditor({
    ...MONACO_READONLY_OPTIONS,
    language: 'javascript',
    defaultValue: currentTabOperationScriptValue,
  });

  const canSeeOperationScripts = graphRef || (isSandboxRoute && isLoggedIn);

  return (
    <div className="mt-2 flex flex-1 flex-col items-center overflow-auto">
      {canSeeOperationScripts ? (
        <>
          {currentTabOperationScriptValue ? (
            <div className="w-full">
              <div className="mb-3 w-full text-sm text-secondary">
                {isOperationScriptEnabled ? (
                  <>
                    This script <span className="font-semibold">will</span> run{' '}
                    {scriptType === 'preflightOperation' ? 'before' : 'after'}{' '}
                    each operation submitted. To turn it off, head over to your
                  </>
                ) : (
                  <>
                    This script is currently off and{' '}
                    <span className="font-semibold">will not</span> run{' '}
                    {scriptType === 'preflightOperation' ? 'before' : 'after'}{' '}
                    each operation submitted. To turn it on, head over to your
                  </>
                )}{' '}
                <ClickableText
                  as="a"
                  onClick={() => {
                    setDocsTab('Explorer Settings');
                  }}
                  href="#scripts"
                  className="inline cursor-pointer text-link hover:underline"
                >
                  Personal Settings
                </ClickableText>
                .
              </div>
              <div
                // Exclude scripts from fullstory
                className="fs-exclude mb-3 h-32 w-full rounded bg-primary"
                data-testid="operation_script_pane_editor"
              >
                <StudioEditorContainer
                  editor={readOnlyOperationScriptsEditor}
                  className={classnames(
                    'h-full',
                    !isOperationScriptEnabled && 'opacity-50',
                  )}
                />
              </div>
            </div>
          ) : scriptType === 'preflightOperation' ? (
            <div className="mb-2 overflow-auto text-center text-sm text-secondary lg:mx-9">
              Write scripts to populate this operation's variables with the
              response from other operations. Great for testing!
            </div>
          ) : (
            <div className="mb-2 overflow-auto text-center text-sm text-secondary lg:mx-9">
              Write scripts to populate environment variables with the response
              from this operation. Great for testing!
            </div>
          )}
          <div className="mt-auto flex">
            <Button
              variant="hidden"
              data-testid={`open_operation_script_modal_${scriptType}`}
              as={Link}
              to={setOverlayVisibleLocation({
                location,
                overlayName:
                  scriptType === 'preflightOperation'
                    ? Config.modals.preflightOperationScript
                    : Config.modals.postflightOperationScript,
              })}
              leftIcon={
                currentTabOperationScriptValue ? <IconEdit /> : <IconPlus />
              }
            >
              {currentTabOperationScriptValue ? 'Edit script' : 'Add script'}
            </Button>
          </div>
        </>
      ) : (
        isSandboxRoute &&
        !isLoggedIn && (
          <>
            <div className="text-sm text-secondary">
              Log in to your Studio account to use operation scripts.
            </div>
            <SandboxLogInButton
              userSegmentType="sandbox_operation_scripts"
              className="mt-4"
            />
          </>
        )
      )}
    </div>
  );
};
